import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  TeamReportResponse,
  TeamMember,
  TestResult,
  SummaryCounts,
  MemberOverallStatus,
  TestResultStatus
} from '../models/team-report.model';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * TeamReportService
 *
 * Frontend service for fetching team safety test report data.
 * Communicates with the backend team report API endpoints.
 *
 * Reference: /agent-os/specs/2026-01-15-team-report-safety-tests/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class TeamReportService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  /**
   * Get team safety test report for an organization
   *
   * @param orgId Organization ID
   * @returns Observable with API response containing TeamReportResponse
   */
  getTeamReport(orgId: string): Observable<ApiResponse<TeamReportResponse>> {
    return this.http.get<ApiResponse<TeamReportResponse>>(
      `${this.apiUrl}/organizations/${orgId}/team-report`
    );
  }

  /**
   * Export team report as CSV
   *
   * @param orgId Organization ID
   * @returns Observable with Blob for CSV download
   */
  exportCsv(orgId: string): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/organizations/${orgId}/team-report/export/csv`,
      { responseType: 'blob' }
    );
  }

  /**
   * Export team report as PDF
   *
   * @param orgId Organization ID
   * @param type PDF type: 'summary' or 'detailed'
   * @returns Observable with Blob for PDF download
   */
  exportPdf(orgId: string, type: 'summary' | 'detailed' = 'summary'): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/organizations/${orgId}/team-report/export/pdf`,
      {
        params: { type },
        responseType: 'blob'
      }
    );
  }

  /**
   * Helper method to trigger file download from Blob
   *
   * @param blob The blob data
   * @param filename The filename for the download
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get status text for display
   *
   * @param status Member or test status
   * @returns Human-readable status text
   */
  getStatusText(status: MemberOverallStatus | TestResultStatus): string {
    switch (status) {
      case 'current':
        return 'Current';
      case 'expired':
        return 'Expired';
      case 'expiring_soon':
        return 'Expiring Soon';
      case 'in_progress':
        return 'In Progress';
      case 'not_started':
        return 'Not Started';
      case 'passed':
        return 'Passed';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  }

  /**
   * Get CSS class for member status badge
   *
   * @param status Member overall status
   * @returns CSS class name
   */
  getMemberStatusClass(status: MemberOverallStatus): string {
    switch (status) {
      case 'current':
        return 'badge-current';
      case 'expired':
        return 'badge-expired';
      case 'in_progress':
        return 'badge-incomplete';
      case 'not_started':
        return 'badge-incomplete';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get CSS class for test status badge
   *
   * @param status Test result status
   * @returns CSS class name
   */
  getTestStatusClass(status: TestResultStatus): string {
    switch (status) {
      case 'passed':
        return 'test-passed';
      case 'failed':
        return 'test-failed';
      case 'expired':
        return 'test-expired';
      case 'expiring_soon':
        return 'test-expiring';
      case 'in_progress':
        return 'test-in-progress';
      case 'not_started':
        return 'test-not-started';
      default:
        return '';
    }
  }

  /**
   * Get icon class for test status
   *
   * @param status Test result status
   * @returns Bootstrap icon class
   */
  getTestStatusIcon(status: TestResultStatus): string {
    switch (status) {
      case 'passed':
        return 'bi-check-circle-fill';
      case 'failed':
        return 'bi-x-circle-fill';
      case 'expired':
        return 'bi-exclamation-circle-fill';
      case 'expiring_soon':
        return 'bi-exclamation-triangle-fill';
      case 'in_progress':
        return 'bi-hourglass-split';
      case 'not_started':
        return 'bi-circle';
      default:
        return 'bi-circle';
    }
  }

  /**
   * Calculate days until expiration
   *
   * @param expiresAt Expiration date string
   * @returns Number of days until expiration (negative if expired)
   */
  getDaysUntilExpiration(expiresAt: string | null): number {
    if (!expiresAt) return 0;
    const expDate = new Date(expiresAt);
    const now = new Date();
    return Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }

  /**
   * Check if a date is within the expiring soon threshold
   *
   * @param expiresAt Expiration date string
   * @param threshold Days threshold (default: 30)
   * @returns True if expiring within threshold
   */
  isExpiringSoon(expiresAt: string | null, threshold: number = 30): boolean {
    const days = this.getDaysUntilExpiration(expiresAt);
    return days > 0 && days <= threshold;
  }

  /**
   * Get passed test count for a member
   *
   * @param member Team member
   * @returns Number of passed tests
   */
  getMemberPassedCount(member: TeamMember): number {
    return member.testResults.filter(
      t => t.status === 'passed' || t.status === 'expiring_soon'
    ).length;
  }

  /**
   * Get completion percentage for a member
   *
   * @param member Team member
   * @returns Completion percentage (0-100)
   */
  getCompletionPercentage(member: TeamMember): number {
    const passed = this.getMemberPassedCount(member);
    const total = member.testResults.length;
    return total > 0 ? Math.round((passed / total) * 100) : 0;
  }
}
